import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../services/user-service";
import { useAuth } from "../context/AuthContext";

const menuItemClasses =
    "block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none";
const dividerClasses = "my-1 border-t border-gray-200";

export default function UserMenu() {
    const { user, clearSession } = useAuth();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const initials = useMemo(() => {
        const firstInitial = user?.firstName?.[0]?.toUpperCase() ?? "";
        const lastInitial = user?.lastName?.[0]?.toUpperCase() ?? "";
        const combined = `${firstInitial}${lastInitial}`;

        if (combined) return combined;
        if (user?.email) return user.email[0].toUpperCase();
        return "U";
    }, [user]);

    const handleToggle = () => setOpen(v => !v);
    const handleClose = () => setOpen(false);

    const handleLogout = () => {
        if (user?.email) {
            logout(user.email);
        }
        clearSession();
        navigate("/login");
    };

    useEffect(() => {
        if (!open) return;
        function onClick(e: MouseEvent) {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                btnRef.current &&
                !btnRef.current.contains(e.target as Node)
            ) {
                handleClose();
            }
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape") {
                e.stopPropagation();
                handleClose();
                btnRef.current?.focus();
            }
        }
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const onButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
            setTimeout(() => {
                const first = menuRef.current?.querySelector<HTMLButtonElement | HTMLAnchorElement>("[data-menu-item]");
                first?.focus();
            }, 0);
        }
    };

    return (
        <div className="relative">
            <button
                data-testid="menu-btn"
                ref={btnRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={handleToggle}
                onKeyDown={onButtonKeyDown}
                className="flex items-center gap-2 rounded-full border border-gray-200 px-2 py-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-crumborange"
            >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-crumbblue text-white font-semibold">
                {initials}
                </span>
                <svg
                    className={`h-4 w-4 text-gray-600 transition-transform ${open ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {open && (
                <div
                    ref={menuRef}
                    data-testid="user-menu"
                    role="menu"
                    aria-label="User menu"
                    className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 overflow-hidden z-50"
                >
                    <div className="px-4 py-3" data-testid='signin-display'>
                        <p className="text-sm text-gray-500">Signed in as</p>
                        <p className="mt-1 truncate text-sm font-medium text-gray-900">
                            {user?.email || "User"}
                        </p>
                    </div>

                    <div className={dividerClasses} />

                    <div className="py-1">
                        <Link
                            to="/settings/profile"
                            role="menuitem"
                            data-menu-item
                            className={menuItemClasses}
                            onClick={handleClose}
                        >
                            Profile Settings
                        </Link>
                        <Link
                            to="/settings/subscription"
                            role="menuitem"
                            data-menu-item
                            className={menuItemClasses}
                            onClick={handleClose}
                        >
                            Subscription & Billing
                        </Link>
                    </div>

                    <div className={dividerClasses} />

                    <div className="py-1">
                        <button
                            data-testid="logout-btn"
                            role="menuitem"
                            data-menu-item=""
                            className={`${menuItemClasses} text-red-600`}
                            onClick={handleLogout}
                        >
                            Log Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}